import React, { useState } from 'react';
import {
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import citaService from '../../services/citaService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const WhatsAppReminderButton = ({ 
  citaId, 
  disabled = false, 
  size = 'small',
  cita = null, // Opcionalmente recibir toda la cita
  onSuccess = null 
}) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  const handleSendReminder = async () => {
   
    
    try {
      setLoading(true);
      
      // Si tenemos los datos de la cita, construir el mensaje aquí
      if (cita && cita.paciente && cita.paciente.whatsapp) {
       
        // Verificar que el número no esté vacío
        const whatsappRaw = cita.paciente.whatsapp.trim();
        
        
        if (!whatsappRaw) {
           
          showNotification('El paciente no tiene número de WhatsApp registrado', 'error');
          setLoading(false);
          return;
        }
        
        // Formatear la fecha correctamente
        const fechaCita = new Date(cita.fecha);
        fechaCita.setHours(fechaCita.getHours() + 5); // Ajustar timezone si es necesario
        const fecha = format(fechaCita, "EEEE d 'de' MMMM", { locale: es });
        
        const hora = formatTime(cita.hora_inicio);
        const especialidad = cita.especialidad?.nombre || 'la consulta';
        const especialista = cita.especialista?.usuario 
          ? `${cita.especialista.usuario.nombre} ${cita.especialista.usuario.apellidos}`
          : 'el especialista';
        
      
        
        const mensaje = `🧠Neuropsico te Recuerda
Estimada familia:
Queremos recordarle que tiene programada su sesión el día ${fecha} a horas ${hora} en el área de ${especialidad} con la Ps. ${especialista} 🫂.
👉 Los horarios son fijos. Si necesita reprogramar, le pedimos hacerlo con al menos 24 horas de anticipación, coordinando directamente con recepción.
👉 En caso de no asistir a la sesión (sea que se confirme o no con anticipación), se aplicará una compensación por inasistencia de S/ 25.00, dado que el especialista mantiene el espacio reservado y preparado especialmente para su menor.
👉 Para evitar el pago de la compensación, puede recuperar la sesión dentro de la misma semana, coordinando otro horario disponible con recepción. Si la sesión no se recupera ni se cancela la compensación, el horario quedará liberado y se asignará a otro consultante, debiendo tomar uno nuevo entre los disponibles.
👉 Le recordamos también la importancia de la puntualidad: la sesión tiene una duración de 60 minutos, con una tolerancia máxima de 15 minutos.

🕓 En algunas ocasiones, las sesiones pueden iniciar con una breve demora debido a que el especialista se encuentra finalizando el proceso del paciente anterior. En NeuroPsico consideramos fundamental que cada consultante pueda cerrar su sesión de manera regulada y tranquila, especialmente en casos que requieren un acompañamiento emocional más profundo.
💜 Agradecemos su comprensión y tolerancia en estas situaciones; si existiera alguna demora, nuestro equipo le avisará oportunamente, y Ud. será atendido el tiempo completo que le corresponde.

💜 Agradecemos su confianza, compromiso y disposición para mantener juntos un proceso terapéutico humano, respetuoso y profesional.

Por favor, responda con un “Sí” para confirmar su asistencia.

🧠 Atentamente,
Centro de Psicología Clínica Integral NeuroPsico`;

  
        
        // Generar URL de WhatsApp
        let telefono = whatsappRaw.replace(/\D/g, '');
        
        // Si el número no empieza con código de país, agregar 51 (Perú)
        if (!telefono.startsWith('51') && telefono.length <= 9) {
          telefono = '51' + telefono;
        }
        
        
        const mensajeCodificado = encodeURIComponent(mensaje);
        const whatsappUrl = `https://wa.me/${telefono}?text=${mensajeCodificado}`;
        
        try {
  // Abrir WhatsApp directamente en nueva ventana
  window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  
  // Marcar como enviado
  setReminderSent(true);
  localStorage.setItem(`reminder_sent_${citaId}`, 'true');
  
  showNotification('WhatsApp abierto. Envíe el mensaje desde allí.', 'info');
  
  if (onSuccess) {
    onSuccess();
  }
  
} catch (error) {
  console.error('Error al abrir WhatsApp:', error);
  showNotification('Error al abrir WhatsApp', 'error');
}
      } else {
              
        const response = await citaService.enviarRecordatorio(citaId);
        
        if (response.data.whatsappUrl) {
           
          window.open(response.data.whatsappUrl, '_blank');
          setReminderSent(true);
          localStorage.setItem(`reminder_sent_${citaId}`, 'true');
          showNotification('WhatsApp abierto. Envíe el mensaje desde allí.', 'info');
        } else {
          
          showNotification('Error al obtener el enlace de WhatsApp', 'error');
        }
      }
    } catch (error) {
      console.error('❌ Error al enviar recordatorio:', error);
      console.error('Stack trace:', error.stack);
      showNotification('Error al preparar el recordatorio', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Verificar si ya se envió el recordatorio
  React.useEffect(() => {
    const sent = localStorage.getItem(`reminder_sent_${citaId}`);
    if (sent === 'true') {
      setReminderSent(true);
    }
  }, [citaId]);

  return (
    <Tooltip title={reminderSent ? "Recordatorio enviado" : "Enviar recordatorio por WhatsApp"}>
      <span>
        <IconButton
          size={size}
          color={reminderSent ? "success" : "primary"}
          onClick={() => {
            
            handleSendReminder();
          }}
          disabled={disabled || loading}
          sx={{ 
            backgroundColor: reminderSent ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: reminderSent ? 'rgba(76, 175, 80, 0.2)' : 'rgba(25, 118, 210, 0.1)'
            }
          }}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <WhatsAppIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default WhatsAppReminderButton;